// src/services/supabaseClient.ts
import { createClient } from "@supabase/supabase-js";

// Variables d'environnement
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY || process.env.SUPABASE_SERVICE_ROLE_KEY;

// Détection de l'environnement
const isProduction = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENVIRONMENT === 'production';
const isAWS = process.env.AWS_REGION || process.env.REACT_APP_AWS_DEPLOYMENT; // Détecte si on est sur AWS

// Logique de sélection de la clé
let selectedKey: string;
let clientType: string;

if (isProduction || isAWS) {
    // En production (AWS), utiliser la Service Role Key
    if (!supabaseServiceKey) {
        throw new Error('Service Role Key manquante pour le déploiement en production AWS');
    }
    selectedKey = supabaseServiceKey;
    clientType = 'Service Role (Production AWS)';
} else {
    // En développement, utiliser l'Anon Key
    if (!supabaseAnonKey) {
        throw new Error('Anon Key manquante pour le développement');
    }
    selectedKey = supabaseAnonKey;
    clientType = 'Anon Key (Développement)';
}

// Vérification de l'URL
if (!supabaseUrl) {
    throw new Error('REACT_APP_SUPABASE_URL manquante dans les variables d\'environnement');
}

// Configuration du client selon l'environnement
const clientConfig = isProduction || isAWS ? {
    // Configuration pour production avec Service Role Key
    auth: {
        autoRefreshToken: false,
        persistSession: false
    },
    global: {
        headers: {
            'User-Agent': 'UDB-Production/1.0.0'
        }
    }
} : {
    // Configuration pour développement avec Anon Key
    auth: {
        persistSession: false,
    },
    global: {
        headers: {
            'User-Agent': 'UDB-Development/1.0.0'
        }
    }
};

// Création du client Supabase
export const supabase = createClient(supabaseUrl, selectedKey, clientConfig);

// Alias pour compatibilité (pointe toujours vers le même client)
export const supabaseAdmin = supabase;

// Types TypeScript pour la base de données
export interface Database {
    public: {
        Tables: {
            members: {
                Row: {
                    id: string;
                    member_number: string;
                    first_name: string;
                    last_name: string;
                    email: string;
                    phone: string;
                    nip: string | null;
                    country: string;
                    province: string;
                    department: string | null;
                    city: string;
                    profession: string | null;
                    selfie_url: string | null;
                    signature_data: string | null;
                    referral_code: string;
                    referred_by: string | null;
                    referrals_count: number;
                    badges: string[];
                    status: string;
                    created_at: string;
                    updated_at: string;
                };
                Insert: {
                    id?: string;
                    member_number: string;
                    first_name: string;
                    last_name: string;
                    email: string | null;
                    phone: string;
                    nip?: string | null;
                    country: string;
                    province: string;
                    department?: string | null;
                    city: string;
                    profession?: string | null;
                    selfie_url?: string | null;
                    signature_data?: string | null;
                    referral_code: string;
                    referred_by?: string | null;
                    referrals_count?: number;
                    badges?: string[];
                    status?: string;
                };
                Update: {
                    id?: string;
                    member_number?: string;
                    first_name?: string;
                    last_name?: string;
                    email?: string;
                    phone?: string;
                    city?: string;
                    province?: string;
                    profession?: string | null;
                    selfie_url?: string | null;
                    referral_code?: string;
                    referred_by?: string | null;
                    referrals_count?: number;
                    badges?: string[];
                    status?: string;
                    updated_at?: string;
                    signature_data?: string | null;
                };
            };
            global_stats: {
                Row: {
                    id: number;
                    total_members: number;
                    total_referrals: number;
                    assembly_attendees: number;
                    updated_at: string;
                };
            };
            provinces: {
                Row: {
                    id: number;
                    name: string;
                    code: string;
                    region: string | null;
                    population: number | null;
                    created_at: string;
                };
            };
        };
    };
}

// Helper functions pour les interactions Supabase
export class SupabaseService {

    // Test de connexion avec info sur le type de client utilisé
    static async testConnection(): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('global_stats')
                .select('total_members')
                .limit(1);

            console.log(`Test connexion Supabase (${clientType}):`, { success: !error, error });
            return !error;
        } catch (error) {
            console.error('Erreur de connexion Supabase:', error);
            return false;
        }
    }

    // Obtenir les statistiques globales
    static async getGlobalStats() {
        try {
            const { data, error } = await supabase
                .from('global_stats')
                .select('*')
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error('Erreur récupération stats:', error);
            return null;
        }
    }

    // Vérifier si un email existe déjà
    static async emailExists(email: string): Promise<boolean> {
        try {
            const { data, error } = await supabase
                .from('members')
                .select('id')
                .eq('email', email.toLowerCase())
                .single();

            return !!data && !error;
        } catch (error) {
            return false;
        }
    }

    // Valider un code de parrainage
    static async validateReferralCode(code: string) {
        try {
            const { data, error } = await supabase
                .from('members')
                .select('id, first_name, last_name, city, province')
                .eq('referral_code', code)
                .single();

            if (error || !data) {
                return { valid: false, referrer: null };
            }

            return {
                valid: true,
                referrer: {
                    firstName: data.first_name,
                    lastName: data.last_name,
                    city: data.city,
                    province: data.province
                }
            };
        } catch (error) {
            console.error('Erreur validation code:', error);
            return { valid: false, referrer: null };
        }
    }

    // Upload d'image vers Storage
    static async uploadImage(file: File | Blob, fileName: string): Promise<string | null> {
        try {
            console.log(`Upload avec ${clientType}`);

            const { error } = await supabase.storage
                .from('member-photos')
                .upload(`selfies/${fileName}`, file, {
                    contentType: 'image/jpeg',
                    upsert: false
                });

            if (error) {
                console.error('Erreur upload:', error);
                throw error;
            }

            // Obtenir l'URL publique
            const { data: { publicUrl } } = supabase.storage
                .from('member-photos')
                .getPublicUrl(`selfies/${fileName}`);

            console.log('Upload réussi:', publicUrl);
            return publicUrl;
        } catch (error) {
            console.error('Erreur upload image:', error);
            return null;
        }
    }

    // Créer un nouveau membre
    static async createMember(memberData: Database['public']['Tables']['members']['Insert']) {
        try {
            const { data, error } = await supabase
                .from('members')
                .insert(memberData)
                .select()
                .single();

            if (error) throw error;
            return { success: true, member: data };
        } catch (error: any) {
            console.error('Erreur création membre:', error);
            return {
                success: false,
                error: error.message || 'Erreur lors de la création du membre'
            };
        }
    }

    // Mettre à jour les stats d'un parrain
    static async updateReferrerStats(referralCode: string) {
        try {
            // Récupérer le parrain
            const { data: referrer, error: referrerError } = await supabase
                .from('members')
                .select('id, referrals_count, badges')
                .eq('referral_code', referralCode)
                .single();

            if (referrerError || !referrer) {
                console.error('Parrain non trouvé:', referralCode);
                return;
            }

            // Incrémenter le compteur
            const newCount = referrer.referrals_count + 1;
            const newBadges = [...referrer.badges];

            // Ajouter des badges selon le nombre de parrainages
            if (newCount >= 3 && !newBadges.includes('Recruteur')) {
                newBadges.push('Recruteur');
            }
            if (newCount >= 5 && !newBadges.includes('Bâtisseur Or')) {
                newBadges.push('Bâtisseur Or');
            }
            if (newCount >= 10 && !newBadges.includes('Ambassadeur')) {
                newBadges.push('Ambassadeur');
            }
            if (newCount >= 25 && !newBadges.includes('Légende')) {
                newBadges.push('Légende');
            }

            // Mettre à jour
            const { error: updateError } = await supabase
                .from('members')
                .update({
                    referrals_count: newCount,
                    badges: newBadges,
                    updated_at: new Date().toISOString()
                })
                .eq('id', referrer.id);

            if (updateError) {
                console.error('Erreur mise à jour parrain:', updateError);
            }

        } catch (error) {
            console.error('Erreur updateReferrerStats:', error);
        }
    }

    // Récupérer un membre par ses informations d'identification
    static async getMemberByCredentials(firstName: string, lastName: string, memberNumber: string) {
        try {
            const { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('first_name', firstName.trim())
                .eq('last_name', lastName.trim())
                .eq('member_number', memberNumber.trim())
                .single();

            if (error || !data) {
                return { success: false, member: null, error: 'Informations incorrectes' };
            }

            return { success: true, member: data, error: null };
        } catch (error: any) {
            console.error('Erreur getMemberByCredentials:', error);
            return { success: false, member: null, error: 'Erreur lors de la vérification' };
        }
    }

    // Récupérer un membre par son code de parrainage
    static async getMemberByReferralCode(code: string) {
        try {
            const { data, error } = await supabase
                .from('members')
                .select('*')
                .eq('referral_code', code)
                .single();

            if (error || !data) return null;
            return data;
        } catch (error) {
            console.error('Erreur récupération membre:', error);
            return null;
        }
    }

    // Méthode utile pour connaître le type de client utilisé
    static getClientInfo() {
        return {
            type: clientType,
            environment: isProduction ? 'production' : 'development',
            isAWS: isAWS || false,
            hasServiceRole: !!supabaseServiceKey,
            hasAnonKey: !!supabaseAnonKey
        };
    }
}

// Log pour debugging
if (process.env.NODE_ENV === 'development') {
    console.log('🔧 Configuration Supabase:');
    console.log('- URL:', supabaseUrl);
    console.log('- Type de client:', clientType);
    console.log('- Environnement:', isProduction ? 'Production' : 'Développement');
    console.log('- AWS détecté:', isAWS || false);
    console.log('- Service Key disponible:', !!supabaseServiceKey);
    console.log('- Anon Key disponible:', !!supabaseAnonKey);
    console.log('- Variables env Supabase:', Object.keys(process.env).filter(key => key.includes('SUPABASE')));
}

export default supabase;
